'use client';

import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CTABanner from '@/components/CTABanner';

const guideData: Record<string, { title: string; description: string; image: string; alt: string; content: string[] }> = {
  'working-capital-loans': {
    title: 'Working Capital Loans',
    description: 'Working capital loans are perfect for small business owners who have cash flow problems or need to purchase equipment or inventory.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1fba19133-1770904564198.png',
    alt: 'Business owner reviewing cash flow statements and financial reports at a desk',
    content: [
      'Working capital loans are short-term financing solutions designed to cover a business\'s everyday operational expenses. Unlike long-term loans used for major investments, working capital loans help businesses manage their day-to-day cash flow needs.',
      'These loans are ideal for businesses that experience seasonal fluctuations in revenue, need to purchase inventory before a busy season, or face unexpected expenses that strain their cash reserves.',
      'Working capital loans typically have shorter repayment terms (3–18 months) and can be funded quickly — sometimes within 24 hours. They are available to businesses with as little as 6 months of operating history.',
      'Common uses include: payroll, rent, utilities, inventory purchases, marketing campaigns, and bridging gaps between accounts receivable and accounts payable.',
      'To qualify, most lenders look for a minimum of $10,000 in monthly revenue, a credit score of 550 or higher, and at least 6 months in business. Rates typically range from 8% to 40% APR depending on your creditworthiness.',
    ],
  },
  'business-line-of-credit': {
    title: 'A Business Line of Credit',
    description: 'Business Line of Credit offers financial flexibility to cover gaps in the standard business cash cycle.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1ccc07fbf-1768482594946.png',
    alt: 'Credit card and financial documents representing a business line of credit',
    content: [
      'A business line of credit is a flexible financing option that allows you to borrow up to a set credit limit and only pay interest on what you use. It works similarly to a credit card but typically offers higher limits and lower interest rates.',
      'Unlike a term loan where you receive a lump sum upfront, a line of credit lets you draw funds as needed, repay them, and draw again. This revolving nature makes it ideal for managing cash flow fluctuations.',
      'Business lines of credit are available in secured and unsecured forms. Secured lines require collateral (such as business assets or real estate) but offer lower rates. Unsecured lines are easier to obtain but typically carry higher interest rates.',
      'Typical credit limits range from $10,000 to $500,000 for small businesses. Interest rates range from 7% to 25% APR. Most lenders require at least 1 year in business and $50,000 in annual revenue.',
      'Best uses include: managing seasonal cash flow, covering unexpected expenses, taking advantage of time-sensitive opportunities, and bridging gaps between invoicing and payment collection.',
    ],
  },
};

// Default content for guides without specific data
function getDefaultContent(title: string, description: string): string[] {
  return [
    `${title} is an important financing option for small business owners looking to grow and manage their operations effectively.`,
    description,
    'Understanding your financing options is crucial to making the right decision for your business. Each loan product has unique features, requirements, and use cases that make it more or less suitable depending on your situation.',
    'When evaluating this type of financing, consider factors such as: your credit score, time in business, annual revenue, how quickly you need funds, and how you plan to use the money.',
    'Lendo works with a network of 200+ lenders to find the best match for your specific needs. Our loan matching process takes just minutes and has no impact on your credit score.',
    'Ready to explore your options? Apply now to see personalized offers from multiple lenders in minutes.',
  ];
}

export default function GuideDetailPage({ params }: { params: { slug: string } }) {
  const guide = guideData[params.slug];
  const allGuides = [
    { title: 'Working Capital Loans', slug: 'working-capital-loans' },
    { title: 'Business Line of Credit', slug: 'business-line-of-credit' },
    { title: 'Merchant Cash Advance', slug: 'merchant-cash-advance' },
    { title: 'Term Loans', slug: 'term-loans' },
    { title: 'Equipment Financing', slug: 'equipment-financing' },
    { title: 'Business Credit', slug: 'business-credit' },
  ];

  const title = guide?.title || params.slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const description = guide?.description || 'A comprehensive guide to help you understand your financing options.';
  const content = guide?.content || getDefaultContent(title, description);
  const image = guide?.image || 'https://img.rocket.new/generatedImages/rocket_gen_img_1fba19133-1770904564198.png';
  const alt = guide?.alt || `${title} guide illustration`;

  return (
    <>
      <Navbar />
      <main style={{ paddingTop: '88px', minHeight: '100vh', background: '#f7f9fc' }}>
        {/* Hero */}
        <div style={{ background: '#1a2b4a', padding: '60px 24px' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <Link href="/guides" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '20px' }}>
              ← Back to Guides
            </Link>
            <h1 style={{ fontFamily: 'Merriweather, serif', fontWeight: 900, fontSize: '2.2rem', color: '#ffffff', margin: '0 0 12px', lineHeight: 1.2 }}>
              {title}
            </h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', color: 'rgba(255,255,255,0.75)', fontSize: '1rem', margin: 0, maxWidth: '600px', lineHeight: 1.6 }}>
              {description}
            </p>
          </div>
        </div>

        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '48px 24px 80px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '32px', alignItems: 'flex-start' }}>

            {/* Main Content */}
            <div>
              {/* Hero image */}
              <div style={{ borderRadius: '16px', overflow: 'hidden', marginBottom: '32px', height: '280px' }}>
                <img src={image} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>

              {/* Article content */}
              <div style={{ background: '#ffffff', borderRadius: '16px', padding: '40px', border: '1px solid #e8edf3', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                {content.map((paragraph, i) => (
                  <p key={i} style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.97rem', color: '#4a5568', lineHeight: 1.85, margin: i < content.length - 1 ? '0 0 20px' : 0 }}>
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* CTA */}
              <div style={{ background: 'linear-gradient(135deg, #00b28a 0%, #009970 100%)', borderRadius: '16px', padding: '32px', marginTop: '24px', textAlign: 'center' }}>
                <h3 style={{ fontFamily: 'Merriweather, serif', fontWeight: 700, fontSize: '1.3rem', color: '#ffffff', margin: '0 0 10px' }}>
                  Ready to get funded?
                </h3>
                <p style={{ fontFamily: 'DM Sans, sans-serif', color: 'rgba(255,255,255,0.85)', fontSize: '0.92rem', margin: '0 0 20px' }}>
                  Apply in minutes and see personalized offers from 200+ lenders. No impact to your credit score.
                </p>
                <Link href="/apply" style={{ display: 'inline-block', background: '#ffffff', color: '#00b28a', borderRadius: '50px', padding: '12px 32px', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '0.95rem', textDecoration: 'none' }}>
                  Apply Now →
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <div style={{ position: 'sticky', top: '100px' }}>
              <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', border: '1px solid #e8edf3', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', marginBottom: '20px' }}>
                <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#1a2b4a', margin: '0 0 16px' }}>More Guides</h3>
                {allGuides.filter((g) => g.slug !== params.slug).map((g) => (
                  <Link key={g.slug} href={`/guides/${g.slug}`}
                    style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: '#4a5568', textDecoration: 'none', padding: '8px 0', borderBottom: '1px solid #f0f4f8', transition: 'color 0.2s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#00b28a')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#4a5568')}>
                    {g.title} →
                  </Link>
                ))}
              </div>

              <div style={{ background: '#f0faf7', borderRadius: '16px', padding: '24px', border: '1px solid #b2e8d8' }}>
                <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#1a2b4a', margin: '0 0 8px' }}>Need help choosing?</h3>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', color: '#4a5568', margin: '0 0 16px', lineHeight: 1.6 }}>Our loan matching tool finds the best options for your specific situation.</p>
                <Link href="/apply" style={{ display: 'block', background: '#00b28a', color: '#ffffff', borderRadius: '8px', padding: '10px 16px', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.88rem', textDecoration: 'none', textAlign: 'center' }}>
                  Get Matched →
                </Link>
              </div>
            </div>
          </div>
        </div>

        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
